/**
 * src/components/RouteError.tsx
 *
 * What a route renders when its component throws.
 *
 * Until now there was no error boundary anywhere in Observe. TanStack Router
 * fell back to its built-in component, which is the bare string "Something
 * went wrong!" — no message, no stack, nothing to act on. On 2026-09-17 the
 * Deals page threw `r.dcs.toFixed is not a function` and the whole dashboard
 * showed that one line; Tanuj reported it as "the dashboard is broken", which
 * from the outside is exactly what it looked like.
 *
 * Two things this changes:
 *
 *   1. The error is shown. A dashboard whose job is to surface bad data should
 *      not hide the reason it fell over. The message is what turns "it's
 *      broken" into a diagnosis.
 *
 *   2. It is scoped. This renders inside the Layout outlet, so the sidebar and
 *      every other tab keep working. One malformed row costs one panel.
 *
 * It does not make the underlying value valid — `txt()` and `num()` in
 * lib/format.ts do that at the render sites. This is the net underneath them,
 * for the next field nobody guarded.
 */
import { useRouter } from '@tanstack/react-router';

export default function RouteError({ error }: { error: unknown }) {
  const router = useRouter();
  const message = error instanceof Error ? error.message : String(error);

  return (
    <div className="p-6">
      <div className="rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-900/60 dark:bg-red-950/30">
        <h2 className="text-sm font-semibold text-red-800 dark:text-red-300">
          This panel failed to render
        </h2>

        <p className="mt-1 text-xs text-red-700/80 dark:text-red-400/80">
          The rest of the dashboard is unaffected — other tabs still work.
        </p>

        <pre className="mt-3 overflow-x-auto whitespace-pre-wrap rounded bg-red-100/70 p-2 font-mono text-[11px] text-red-900 dark:bg-red-950/50 dark:text-red-200">
          {message}
        </pre>

        <button
          type="button"
          onClick={() => router.invalidate()}
          className="mt-3 rounded border border-red-300 px-2.5 py-1 text-xs font-medium text-red-800 hover:bg-red-100 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/40"
        >
          Retry
        </button>
      </div>
    </div>
  );
}
